import { getTranslation } from '@mahawi/eshop-common/src/translation';
import { type IAdminCategory } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Divider, Form, Row, Select, Space, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getFilterOption, getFilterSort } from '../../fragments/select-filter';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { saveCategoriesUUIDs } from '../../reducers/property/reducer';
import { SPACING } from '../../theme/tokens';
import BubbleComponent from '../bubble';
import Link from '../link';

interface IForm {
  categoryUUIDs: string[];
}

function PropertyCategories(): React.ReactElement {
  const dispatch = useAppDispatch();
  const propertyCode = useSelector((s: RootState) => s.Property.property?.code);
  const propertyCategoryUUIDs = useSelector(
    (s: RootState) => s.Property.property?.category?.uuids,
  );
  const propertyInProcess = useSelector((s: RootState) => s.Property.inProcess);
  const categoriesAll = useSelector((s: RootState) => s.Categories.categories);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [form] = Form.useForm<IForm>();

  const [assignedCategoriesNames, setassignedCategoriesNames] = useState<
    Map<string, string>
  >(new Map<string, string>());
  const [categoriesOptions, setCategoriesOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const onFinish = useCallback(
    ({ categoryUUIDs }: IForm): void => {
      if (propertyCode) {
        dispatch(saveCategoriesUUIDs(propertyCode, categoryUUIDs));
      }
    },
    [propertyCode, dispatch],
  );

  useEffect((): void => {
    const assignedCategoriesNamesUE = new Map<string, string>();

    propertyCategoryUUIDs?.forEach((uuid: string): void => {
      const category: IAdminCategory | undefined = categoriesAll?.find(
        (c: IAdminCategory): boolean => c.uuid === uuid,
      );

      const categoryName: string | null = getTranslation(
        category?.names,
        languageType,
      );

      assignedCategoriesNamesUE.set(uuid, categoryName || uuid);
    });

    setassignedCategoriesNames(assignedCategoriesNamesUE);
  }, [categoriesAll, propertyCategoryUUIDs, languageType?.code]);

  useEffect((): void => {
    const categoriesOptionsUE: { value: string; label: string }[] =
      categoriesAll?.map(
        (category: IAdminCategory): { value: string; label: string } => {
          const categoryName: string | null = getTranslation(
            category.names,
            languageType,
          );

          return {
            value: category.uuid,
            label: categoryName || category.uuid,
          };
        },
      ) || [];

    setCategoriesOptions(categoriesOptionsUE);
  }, [categoriesAll, languageType?.code]);

  return (
    <BubbleComponent span={24}>
      <Space size={SPACING.lg} align="start" orientation="horizontal">
        <strong>Categories</strong>
      </Space>

      <Row gutter={SPACING.lg}>
        <Col xs={24} sm={12}>
          <Form
            form={form}
            onFinish={(values: IForm): void => onFinish(values)}
          >
            <Row>
              <Col xs={24} md={8}>
                <Space size={SPACING.lg} align="start">
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={propertyInProcess}
                  >
                    Submit
                  </Button>
                  <Spin size="small" spinning={propertyInProcess} />
                </Space>
              </Col>
            </Row>

            <Divider />

            <Row>
              <Col span={24}>
                <Form.Item
                  name="categoryUUIDs"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                  label="Type"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select categories"
                    disabled={propertyInProcess}
                    showSearch={{
                      autoClearSearchValue: false,
                      filterOption: getFilterOption,
                      filterSort: getFilterSort,
                    }}
                    options={categoriesOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>

        <Col xs={24} sm={12}>
          <div>
            <strong>Assigned categories</strong>
            {[...assignedCategoriesNames].map(
              ([uuid, name]: [string, string]) => (
                <div key={uuid}>
                  <Link to={`/category/${uuid}`} openInNewTab label={name} />
                </div>
              ),
            )}
          </div>
        </Col>
      </Row>
    </BubbleComponent>
  );
}

export default PropertyCategories;
